import { CHECKLIST, type ChecklistKey } from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle } from 'lucide-react';

export function useChecklist() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['checklist'],
    queryFn: async (): Promise<Record<string, number>> => {
      const r = await fetch('/api/onboarding/checklist');
      return r.ok ? ((await r.json()) as { completed: Record<string, number> }).completed : {};
    },
  });
  const m = useMutation({
    mutationFn: (key: ChecklistKey) => fetch(`/api/onboarding/checklist/${key}`, { method: 'POST' }),
    onMutate: key => qc.setQueryData<Record<string, number>>(['checklist'], prev => ({ ...prev, [key]: Date.now() })),
  });
  const completed = q.data ?? {};
  return {
    completed,
    done: CHECKLIST.filter(c => completed[c.key]).length,
    total: CHECKLIST.length,
    loaded: q.isSuccess,
    complete: (key: ChecklistKey) => {
      if (!completed[key]) m.mutate(key);
    },
  };
}

/** Getting-started checklist with progress (PLAN §12.6). Shown on Home until complete. */
export function GettingStartedChecklist({ hideWhenDone = false }: { hideWhenDone?: boolean }) {
  const { completed, done, total, loaded } = useChecklist();
  if (!loaded || (hideWhenDone && done === total)) return null;
  return (
    <section aria-labelledby="checklist-title" className="rounded-lg border bg-surface p-4">
      <div className="mb-2 flex items-center justify-between">
        <h2 id="checklist-title" className="font-semibold">
          Getting started
        </h2>
        <span className="text-xs text-muted-foreground tabular" data-testid="checklist-progress">
          {done} of {total} done
        </span>
      </div>
      <div
        role="progressbar"
        aria-label="Getting started progress"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        className="mb-3 h-1.5 rounded-full bg-muted"
      >
        <div className="h-full rounded-full bg-primary" style={{ width: `${(done / total) * 100}%` }} />
      </div>
      <ul className="space-y-1 text-sm">
        {CHECKLIST.map(c => (
          <li key={c.key}>
            <a href={c.href} className="flex items-center gap-2 rounded px-1 py-0.5 hover:bg-muted">
              {completed[c.key] ? (
                <CheckCircle2 className="size-4 text-success-foreground" aria-label="Done" />
              ) : (
                <Circle className="size-4 text-muted-foreground" aria-label="Not done" />
              )}
              <span className={completed[c.key] ? 'text-muted-foreground line-through' : ''}>{c.label}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
